import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { displayNameFromFields } from "commons/userDisplayName";
import useSWR, { mutate } from "swr";

export interface Publisher {
  id: string;
  nick_name?: string;
  nickname?: string;
  username?: string;
  state?: number;
  frameCount?: number;
  onlineTemplateCount?: number;
}

interface PainterPickerProps {
  value?: string;
  onSelect: (userId: string, userName: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}

export function PainterPicker({
  value,
  onSelect,
  label,
  placeholder = "选择 Painter",
  className,
}: PainterPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: selected } = useSWR<Publisher>(
    value ? [`/publishers/${value}`] : null,
  );

  const {
    data: candidates = [],
    isLoading,
    error,
  } = useSWR<Publisher[]>(
    open
      ? [
          "/publishers",
          {
            role: "painter",
            available: 1,
            ...(debouncedQ ? { q: debouncedQ } : {}),
          },
        ]
      : null,
  );

  const users = useMemo(() => {
    const map = new Map<string, Publisher>();
    for (const user of candidates) {
      map.set(user.id, user);
    }
    if (selected) {
      map.set(selected.id, selected);
    }
    return Array.from(map.values());
  }, [candidates, selected]);

  const selectedUserName = selected
    ? displayNameFromFields(selected, selected.id)
    : value || null;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch("");
      setDebouncedQ("");
    }
  };

  const handleSelect = (user: Publisher) => {
    const userName = displayNameFromFields(user, user.id);
    mutate([`/publishers/${user.id}`], user, { revalidate: false });
    onSelect(user.id, userName);
    handleOpenChange(false);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label>{label}</Label>}
      <div className="relative">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className={cn(
                "w-full justify-between",
                !value && "text-muted-foreground",
                value && "pr-8",
              )}
            >
              {selectedUserName || placeholder}
              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="搜索昵称..."
                className="h-9"
                value={search}
                onValueChange={setSearch}
              />
              <CommandList className="max-h-60 overflow-y-auto">
                <CommandEmpty>
                  {error
                    ? "查询失败"
                    : isLoading
                      ? "加载中..."
                      : "未找到结果"}
                </CommandEmpty>
                <CommandGroup>
                  {users.map((user) => {
                    const name = displayNameFromFields(user, user.id);
                    return (
                      <CommandItem
                        key={user.id}
                        value={`${name} ${user.id}`}
                        onSelect={() => handleSelect(user)}
                      >
                        {name}
                        <CheckIcon
                          className={cn(
                            "ml-auto h-4 w-4",
                            value === user.id ? "opacity-100" : "opacity-0",
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {value && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 z-10 h-6 w-6 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => onSelect("", "")}
            type="button"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
